/**
 * Public service taxonomy for SEO URLs.
 *
 * The slugs here are a 1:1 mirror of `backend/src/models/Service.js`
 * (`SERVICE_CATEGORIES` / `SUBCATEGORIES`). We deliberately reuse the existing
 * marketplace taxonomy rather than inventing an SEO-only one, so that a
 * `/services/<slug>` URL maps directly onto a real `serviceCategory` filter the
 * discovery API already understands.
 *
 * Only the human-readable labels are new. They exist so page titles read like
 * "Braiders in Austin, TX" rather than "braiding in austin".
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

/**
 * @typedef {Object} ServiceCategory
 * @property {string} slug          URL + API value (matches backend enum)
 * @property {string} label         Short noun ("Braiding")
 * @property {string} providerNoun  What we call the professionals ("Braiders")
 * @property {string} serviceNoun   What we call the work ("braiding services")
 * @property {string[]} subcategories
 */

/** @type {ServiceCategory[]} */
const SERVICE_CATEGORIES = [
  {
    slug: 'hair',
    label: 'Hair',
    providerNoun: 'Hair Stylists',
    serviceNoun: 'hair styling',
    subcategories: ['natural-hair', 'treatments', 'coloring', 'extensions', 'silk-press', 'blowouts'],
  },
  {
    slug: 'braiding',
    label: 'Braiding',
    providerNoun: 'Braiders',
    serviceNoun: 'braiding',
    subcategories: [
      'box-braids',
      'knotless-braids',
      'cornrows',
      'twists',
      'locs',
      'goddess-braids',
      'feed-in',
      'fulani',
      'tribal',
      'faux-locs',
      'passion-twists',
      'kids-braids',
    ],
  },
  {
    slug: 'barber',
    label: 'Barbering',
    providerNoun: 'Barbers',
    serviceNoun: 'barbering',
    subcategories: ['haircuts', 'fades', 'designs', 'beard', 'shaves'],
  },
  {
    slug: 'nails',
    label: 'Nails',
    providerNoun: 'Nail Technicians',
    serviceNoun: 'nail care',
    subcategories: ['manicure', 'pedicure', 'gel', 'acrylic', 'dip-powder', 'nail-art', 'removal'],
  },
  {
    slug: 'makeup',
    label: 'Makeup',
    providerNoun: 'Makeup Artists',
    serviceNoun: 'makeup',
    subcategories: ['full-face', 'bridal', 'special-occasion', 'lessons', 'editorial'],
  },
  {
    slug: 'skincare',
    label: 'Skincare',
    providerNoun: 'Estheticians',
    serviceNoun: 'skincare',
    subcategories: ['facial', 'treatments', 'consultation', 'peels'],
  },
  {
    slug: 'massage',
    label: 'Massage',
    providerNoun: 'Massage Therapists',
    serviceNoun: 'massage therapy',
    subcategories: ['relaxation', 'therapeutic', 'specialty', 'prenatal'],
  },
  {
    slug: 'lashes',
    label: 'Lashes',
    providerNoun: 'Lash Technicians',
    serviceNoun: 'lash services',
    subcategories: ['extensions', 'lift', 'tint'],
  },
  {
    slug: 'brows',
    label: 'Brows',
    providerNoun: 'Brow Artists',
    serviceNoun: 'brow services',
    subcategories: ['shaping', 'tinting', 'microblading', 'lamination'],
  },
  {
    slug: 'waxing',
    label: 'Waxing',
    providerNoun: 'Waxing Specialists',
    serviceNoun: 'waxing',
    subcategories: ['facial', 'body', 'brazilian', 'full-body'],
  },
  {
    slug: 'wellness',
    label: 'Wellness',
    providerNoun: 'Wellness Practitioners',
    serviceNoun: 'wellness services',
    subcategories: ['aromatherapy', 'energy-healing', 'reflexology'],
  },
  {
    slug: 'fitness',
    label: 'Fitness',
    providerNoun: 'Fitness Professionals',
    serviceNoun: 'fitness training',
    subcategories: ['personal-training', 'yoga', 'pilates'],
  },
  {
    slug: 'therapy',
    label: 'Therapy',
    providerNoun: 'Therapy Practitioners',
    serviceNoun: 'therapy services',
    subcategories: ['cupping', 'acupuncture'],
  },
];

const CATEGORY_BY_SLUG = new Map(SERVICE_CATEGORIES.map((c) => [c.slug, c]));

/**
 * `User.stylistProfile.categories` carries one legacy value ("extensions") that
 * predates the Service taxonomy. It rolls up into hair for SEO purposes so we
 * never publish a `/services/extensions` URL the API cannot filter on.
 */
const LEGACY_CATEGORY_ALIASES = {
  extensions: 'hair',
  barbering: 'barber',
  nail: 'nails',
  'hair-braiding': 'braiding',
};

/**
 * @param {string | null | undefined} value
 * @returns {ServiceCategory | null}
 */
function resolveServiceCategory(value) {
  if (value == null) return null;
  const key = String(value).trim().toLowerCase();
  if (!key) return null;
  if (CATEGORY_BY_SLUG.has(key)) return CATEGORY_BY_SLUG.get(key);
  const aliased = LEGACY_CATEGORY_ALIASES[key];
  if (aliased && CATEGORY_BY_SLUG.has(aliased)) return CATEGORY_BY_SLUG.get(aliased);
  return null;
}

/**
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
function isKnownServiceCategory(value) {
  return resolveServiceCategory(value) !== null;
}

/** @returns {string[]} every canonical category slug */
function allCategorySlugs() {
  return SERVICE_CATEGORIES.map((c) => c.slug);
}

module.exports = {
  SERVICE_CATEGORIES,
  LEGACY_CATEGORY_ALIASES,
  resolveServiceCategory,
  isKnownServiceCategory,
  allCategorySlugs,
};
